import React, { useContext, useEffect, useId, useState } from 'react';
import { DropdownBaseItem, MenuItemPopperContainer } from './DropdownBaseItem';
import useRootClose from 'react-overlays/useRootClose';
import { MenuItemPopper } from '../MenuItem/MenuItemPopper';
import { useTestId } from '../../hooks/useTestId/useTestId';
import debounce from 'lodash.debounce';
import { MATERIAL_ICONS, MaterialIcon } from '../../components/Icon/icons';
import { useDropdown } from '../Dropdown/useDropdown';
import { useDropdownSheet } from '../Dropdown/DropdownMenu';
import { IDropdownItemAsButtonProps } from './DropdownItem';
import { useResponsive } from '../../hooks/useResponsive/useResponsive';
import { mergeRefs } from '../../common/utils';
import styled, { css } from 'styled-components';
import { ScrollingContainer } from '../../fundamentals/spaces/ScrollingContainer';
import { DropdownContext } from '../Dropdown/DropdownContext';

const MenuScrollContainer = styled(ScrollingContainer)<{ isExpandedChild?: boolean }>`
  max-height: 70vh;
  ${({ isExpandedChild, theme }) =>
    isExpandedChild &&
    css`
      background: ${theme.dropdownColours.item.hover.background};
    `};
`;

const DropdownChild = React.forwardRef<HTMLButtonElement, IDropdownItemAsButtonProps>((args, ref) => {
  const { generateTestId } = useTestId(args, 'DropdownChild');

  const childRef = React.useRef<HTMLDivElement>(null);
  const [hovered, setHovered] = useState<boolean>(false);
  const { debounceInMs, asMenu } = useDropdown(args);
  const [pinned, setPinned] = useState<boolean | undefined>(false);
  const [trailingIcon, setTrailingIcon] = useState<MaterialIcon>(MATERIAL_ICONS.KeyboardArrowDown);
  const buttonResponsive = useResponsive();
  const id = useId();
  const { setExpandedSectionId, expandedSectionId } = useContext(DropdownContext);

  useEffect(() => {
    setTrailingIcon(
      args.inlineNesting ? (expandedSectionId === id ? MATERIAL_ICONS.KeyboardArrowUp : MATERIAL_ICONS.KeyboardArrowDown) : MATERIAL_ICONS.ChevronRight
    );
  }, [expandedSectionId]);

  // close active submenu when clicked outside
  const handleRootClose = () => {
    setHovered(false);
    // eslint-disable-next-line unicorn/no-useless-undefined
    setPinned(undefined);
  };

  useRootClose(childRef as any, handleRootClose, {
    disabled: !(pinned || hovered)
  });

  const toggleHover = (action: boolean) => {
    if (pinned !== true) {
      // eslint-disable-next-line unicorn/no-useless-undefined
      setPinned(undefined);
      setHovered(action);
    }
  };

  const debouncedHandleMouseOut = debounce(() => {
    toggleHover(false);
  }, debounceInMs);

  const handleMouseOver = () => {
    toggleHover(true);
    debouncedHandleMouseOut.cancel();
  };

  const handleOnClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    // remove focus for non-keyboard
    /* istanbul ignore else */
    if (e.detail) {
      e.currentTarget.blur();
    }

    /* istanbul ignore else */
    if (!args.onClick) {
      hovered && setHovered(false);
      setPinned(!pinned);
      e.stopPropagation();
    }

    args.onClick?.(e);
  };

  const combinedProps = {
    onClick: handleOnClick,
    onMouseOver: handleMouseOver,
    onMouseOut: debouncedHandleMouseOut,
    ...args,
    postTrailingIcon: args.postTrailingIcon ?? { name: trailingIcon },
    active: pinned ?? hovered,
    asMenu,
    ...generateTestId()
  };

  const baseOnClickProps = args.inlineNesting
    ? {
        onClick: () => {
          if (expandedSectionId === id) {
            setExpandedSectionId?.(undefined);
          } else {
            setExpandedSectionId?.(id);
          }
        }
      }
    : {};

  return (
    <>
      <DropdownBaseItem ref={mergeRefs([ref, buttonResponsive.ref])} {...combinedProps} label={undefined} {...baseOnClickProps}>
        {args.label}
      </DropdownBaseItem>
      {args.inlineNesting ? (
        <>{expandedSectionId === id && <MenuScrollContainer isExpandedChild>{args.children}</MenuScrollContainer>}</>
      ) : (
        <MenuItemPopperContainer
          siblingHeight={buttonResponsive.height ?? 1}
          ref={childRef}
          onMouseOver={handleMouseOver}
          onMouseOut={debouncedHandleMouseOut}
          {...generateTestId('child')}
        >
          <MenuItemPopper show={pinned ?? hovered} triggerRef={childRef} zeroBorderRadius={asMenu} popperConfig={args.popperConfig}>
            <MenuScrollContainer>{args.children}</MenuScrollContainer>
          </MenuItemPopper>
        </MenuItemPopperContainer>
      )}
    </>
  );
});

export const DropdownParentItem = React.forwardRef<HTMLButtonElement, IDropdownItemAsButtonProps>((args, ref) => {
  const { generateTestId } = useTestId(args, 'DropdownItemParent');
  let { setContent } = useDropdownSheet();
  const { isMobile } = useDropdown(args);

  const handleOnClick: React.MouseEventHandler = (e) => {
    setContent(args.children);

    e.stopPropagation();
  };

  return isMobile ? (
    <DropdownBaseItem
      ref={ref}
      onClick={handleOnClick}
      {...args}
      postTrailingIcon={args.postTrailingIcon ?? { name: MATERIAL_ICONS.ChevronRight }}
      label={undefined}
      {...generateTestId()}
    >
      {args.label}
    </DropdownBaseItem>
  ) : (
    <DropdownChild ref={ref} {...args} />
  );
});
